import {
    React,
    useState,
    useEffect,
    Drawer,
    DialogActions,
    Box,
    useApi,
    Typography,
    Divider,
    IconButton,
    ArrowBackIosIcon,
    Default_Text_Color
} from '../../../../utils/globalImports';

import {
    CustomButton,
    CustomTextField,
    validationMessages,
    CustomList,
    EntryItem,
    Category,
    Toaster
} from '../../../../utils/globalImports';

import { ApiResponse } from '../../../../services/apiService';

export interface SubCategory {
    id?: number;
    categoryId?: number;
    description: string;
    notes: string;
    createdid?: number;
}

interface ModalProps {
    open: boolean;
    onClose: () => void;
    categoryData: Category;
    subCategoryData?: SubCategory; // Optional prop for editing
}

const AddProjectSubCategory: React.FC<ModalProps> = ({ open, onClose, categoryData, subCategoryData }) => {


    const { execute, loading } = useApi();
    const [entries, setEntries] = useState<EntryItem[]>([]);
    const [editId, setEditId] = useState<number | null>(null);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const userId = Number(localStorage.getItem("userId"));
    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));

    const [formData, setFormData] = useState<SubCategory>({
        description: '',
        notes: ''
    });

    const [errors, setErrors] = useState({
        description: '',
        notes: '',
    });

    // Populate fields if editing a category
    useEffect(() => {
        if (open) {
            if (subCategoryData) {
                setFormData({
                    description: subCategoryData.description || '',
                    notes: subCategoryData.notes || ''
                });
            } else {
                setFormData({
                    description: '',
                    notes: ''
                });
            }

            setErrors({
                description: '', notes: ''
            });
        }
    }, [open, subCategoryData]);


    // Validate input fields
    const validate = () => {
        let isValid = true;
        const errors = { description: '', notes: '' };

        if (!formData.description.trim()) {
            errors.description = validationMessages.common_required("SubCategory name");
            isValid = false;
        }
        else if (formData.description.length > 255) {
            errors.description = validationMessages.common_characterLimit("SubCategory name", 255);
            isValid = false;
        }
        else if (!/^[A-Za-z0-9\s-]+$/.test(formData.description)) {
            errors.description = validationMessages.commen_invalidCategoryString("Category Name");
            isValid = false;
        }
        else if (entries.length > 0) {
            const name = formData.description.trim().toLowerCase();

            const isDuplicate = entries.some(
                entry =>
                    entry.name.trim().toLowerCase() === name &&
                    entry.id !== editId // exclude current entry if editing
            );

            if (isDuplicate) {
                errors.description = validationMessages.common_duplicateRecord("SubCategory name");
                isValid = false;
            }
        }

        if (formData.notes.length > 5000) {
            errors.notes = validationMessages.common_characterLimit("Note", 5000);
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        let response: ApiResponse<any> = { data: null, error: null };

        if (subCategoryData) {

            const updatedSubCategory = {
                id: subCategoryData.id,
                description: formData.description,
                notes: formData.notes,
                updatedId: userId,
            };

            response = await execute('put', `/SubCategory/${subCategoryData.id}`, updatedSubCategory);
        } else {

            // Adding new categories
            const subCategories: SubCategory[] = entries.map(entry => ({
                categoryId: categoryData.id,
                description: entry.name,
                notes: entry.description,
                createdid: userId,
            }));

            response = await execute('post', '/SubCategory', subCategories);
        }

        if (response!) {
            if (response.error) {
                // If there's an error in the response, display the error message
                setToast({ open: true, message: response.error, type: "error" });
            } else {
                // If the response is successful, display the success message
                setToast({ open: true, message: response.data, type: "success" });
                await delay(1000); // Apply delay for 2 seconds if shouldDelay is true
                onClose();
            }
        }
    };

    const handleAddSubFeeStage = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            if (editId !== null) {
                setEntries(prev =>
                    prev.map(entry =>
                        entry.id === editId
                            ? {
                                ...entry,
                                name: formData.description,
                                description: formData.notes
                            }
                            : entry
                    )
                );
                setEditId(null);
            } else {
                const newEntry: EntryItem = {
                    id: Date.now(),
                    name: formData.description,
                    description: formData.notes
                };
                setEntries(prev => [...prev, newEntry]);
            }

            setFormData({
                description: '',
                notes: ''
            });
        }
    }

    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                width: 550,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 500,
                    height: '100%',
                    paddingLeft: 2,
                    paddingRight: 2,
                    paddingTop: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.86)',
                },
            }}
        >
            <Box>
                <Typography
                    sx={{
                        color: Default_Text_Color,
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 2,
                        mt: 2,
                        position: 'relative',
                        fontWeight: 500,
                        fontSize: 22
                    }}
                >
                    {/* Left: Back Button */}
                    <IconButton
                        sx={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'transparent',
                        }}
                        onClick={onClose}
                    >
                        <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
                    </IconButton>
                    {subCategoryData ? 'Edit Subcategory' : 'Add Subcategory'}

                    <CustomButton
                        capsule
                        sx={{
                            textTransform: 'none',
                            width: '82px',
                        }}
                        onClick={handleSubmit}
                    >
                        Submit
                    </CustomButton>

                </Typography>

                <Divider sx={{ mb: 3, mx: "-15px" }} />

                {/* Client Category Name */}
                <Box sx={{ mb: 2 }}>
                    <CustomTextField
                        label="Subcategory Name"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        sx={{
                            mb: 1,
                            backgroundColor: '#fff'
                        }}
                        required={true}
                    />
                    {errors.description && (
                        <Typography className="validationMessage" role="alert">
                            {errors.description}
                        </Typography>
                    )}
                </Box>


                {/* Description */}
                <Box sx={{ mb: 2 }}>
                    <CustomTextField
                        label="Notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        sx={{
                            mb: 1,
                            backgroundColor: '#fff',
                            '& .MuiInputBase-root': {
                                height: 'auto',
                            },
                            '& .MuiOutlinedInput-input': {
                                // minHeight: 30,
                                padding: '10px',
                            },
                        }}
                        required={false}
                        isTextArea={true}
                    />
                    {errors.notes && (
                        <Typography className="validationMessage" role="alert">
                            {errors.notes}
                        </Typography>
                    )}
                </Box>


                {/* Status */}
                {/* {subCategoryData && (
                    <>
                        <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
                        <Box sx={{ display: 'flex' }}>
                            <CustomRadioButton
                                label="Active"
                                value="Active"
                                name="status"
                                checked={formData.status === 'Active'}
                                onChange={handleChange}
                                required={true}
                            />
                            <CustomRadioButton
                                label="Inactive"
                                value="Inactive"
                                name="status"
                                checked={formData.status === 'Inactive'}
                                onChange={handleChange}
                                required={true}
                            />
                        </Box>
                    </>
                )} */}


                {/* Submit Button */}
                {!subCategoryData && (
                    <DialogActions>
                        <CustomButton
                            capsule
                            sx={{
                                textTransform: 'none',
                                width: '282px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                mr: 'auto',
                                ml: 'auto',
                                mt: 2,
                                mb: 5
                            }}
                            onClick={handleAddSubFeeStage}
                        >
                            {editId ? 'Update Subcategory' : 'Add Subcategory'}
                        </CustomButton>
                    </DialogActions>
                )}

            </Box>

            {entries.length > 0 && (
                <CustomList
                    entries={entries}
                    onEdit={(entry) => {
                        setEditId(entry.id);
                        setFormData({
                            description: entry.name || '',
                            notes: entry.description || ''
                        });
                    }}
                    onDelete={(id) => setEntries(prev => prev.filter(entry => entry.id !== id))}
                    heading={categoryData.description}
                >
                </CustomList>
            )}

            {/* Toaster */}
            <Toaster
                open={toast.open}
                message={toast.message}
                type={toast.type as "success" | "error"}
                onClose={() =>
                    setToast({ ...toast, open: false })
                }
            />

        </Drawer>


    );
};

export default AddProjectSubCategory;
